import React, { useState, useEffect, useRef } from "react";
import {
  Heart,
  Star,
  MapPin,
  Wifi,
  Wind,
  Utensils,
  Compass,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import "./HandpickedRomanticStays.css";
import { useNavigate } from "react-router-dom";

const API_ENDPOINT = "https://admin.jayrajgroup.com/api/hotels";

const CATEGORY_TAGS = [
  "Overwater Villa",
  "Private Beach Resort",
  "Mountain Luxury Resort",
  "Heritage Palace Hotel"
];

const HandpickedRomanticStays = () => {
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState({});
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await fetch(API_ENDPOINT);
        const result = await response.json();
        if (result.status && Array.isArray(result.data)) {
          setStays(result.data);
        }
      } catch (error) {
        console.error("Error fetching romantic stays:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, []);

  const toggleWishlist = (id) => {
    setWishlist((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const handleClick = (resort) => {

  const slug = createSlug(resort?.slug || resort?.name || "resort");

  const id = resort?.id;

  const random = Math.floor(100 + Math.random() * 900); // random 3 digit
  const encodedId = id + random;

  const urlPart = `${slug}-${encodedId}-${random}`;

navigate(`/hotel/${urlPart}`);

};

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === "left" ? -380 : 380;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const cleanDescription = (html) => {
    if (!html) return "Experience unmatched luxury and romantic surroundings tailored for unforgettable moments.";
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body.textContent || "";
    return text.length > 130 ? `${text.substring(0, 130)}...` : text;
  };

  const getMinPrice = (rooms) => {
    if (!rooms || rooms.length === 0) return 3500;
    const prices = rooms.map((r) => parseFloat(r.price_per_night)).filter(Boolean);
    return prices.length > 0 ? Math.min(...prices) : 3500;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(price);
  };

  const renderAmenityIcon = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes("wifi")) return <Wifi size={14} />;
    if (lowerName.includes("airconditioner") || lowerName.includes("ac")) return <Wind size={14} />;
    if (lowerName.includes("restaurant") || lowerName.includes("food")) return <Utensils size={14} />;
    if (lowerName.includes("sightseeing")) return <Compass size={14} />;
    return <ShieldCheck size={14} />;
  };

  if (loading) {
    return (
      <div className="hrs-hotel-loader-container">
        <div className="hrs-hotel-ring-spinner"></div>
      </div>
    );
  }

  return (
    <section className="hrs-hotel-main-section">
      <div className="hrs-hotel-content-wrapper">
        {/* Header Block with Slider Controls */}
        <div className="hrs-hotel-header-row">
          <div className="hrs-hotel-header-block">
            <span className="hrs-hotel-tagline-badge">
              <Sparkles size={14} /> EXCLUSIVE ESCAPES
            </span>
            <h2 className="hrs-hotel-title-text">
              Stay Somewhere as Beautiful as Your Love Story
            </h2>
            <p className="hrs-hotel-subtitle-text">
              Discover handpicked luxury sanctuaries designed for intimacy, comfort, and timeless romantic memories.
            </p>
          </div>

          <div className="hrs-hotel-slider-controls">
            <button
              className="hrs-hotel-nav-btn"
              onClick={() => scrollSlider("left")}
              aria-label="Previous stays"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="hrs-hotel-nav-btn"
              onClick={() => scrollSlider("right")}
              aria-label="Next stays"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Horizontal Slider Track */}
        <div className="hrs-hotel-slider-track" ref={sliderRef}>
          {stays.map((stay, index) => {
            const isLiked = !!wishlist[stay.id];
            const minPrice = getMinPrice(stay.rooms);
            const rating = stay.average_rating > 0 ? stay.average_rating : 4.9;
            const categoryBadge = CATEGORY_TAGS[index % CATEGORY_TAGS.length] || "Luxury Stay";

            return (
              <div key={stay.id} className="hrs-hotel-stay-card" onClick={() => handleClick(stay)}>
                {/* Image Frame */}
                <div className="hrs-hotel-card-media">
                  <img
                    src={stay.featured_image || stay.featured_images?.[0]}
                    alt={stay.name}
                    className="hrs-hotel-photo"
                  />
                  <div className="hrs-hotel-badge-layer">
                    <span className="hrs-hotel-category-badge">{categoryBadge}</span>
                  </div>
                  {/* <button
                    className={`hrs-hotel-wishlist-button ${isLiked ? "hrs-hotel-wishlist-active" : ""}`}
                    onClick={() => toggleWishlist(stay.id)}
                    aria-label="Save to Wishlist"
                  >
                    <Heart className="hrs-hotel-heart-icon" />
                  </button> */}
                </div>

                {/* Content Frame */}
                <div className="hrs-hotel-card-details">
                  <div className="hrs-hotel-meta-header">
                    <span className="hrs-hotel-location">
                      <MapPin size={14} /> {stay.location}
                    </span>
                    <span className="hrs-hotel-rating">
                      <Star size={14} className="hrs-hotel-star-icon" /> {rating}
                    </span>
                  </div>

                  <h3 className="hrs-hotel-card-name">{stay.name}</h3>

                  <p className="hrs-hotel-card-description">
                    {cleanDescription(stay.description)}
                  </p>

                  {/* Amenities */}
                  <div className="hrs-hotel-amenities-row">
                    {stay.amenities && stay.amenities.length > 0 ? (
                      stay.amenities.slice(0, 4).map((item) => (
                        <span key={item.id} className="hrs-hotel-amenity-chip" title={item.name}>
                          {renderAmenityIcon(item.name)}
                          <span>{item.name}</span>
                        </span>
                      ))
                    ) : (
                      <span className="hrs-hotel-amenity-chip">
                        <ShieldCheck size={14} /> <span>Premium Services</span>
                      </span>
                    )}
                  </div>

                  {/* Pricing and Action */}
                  <div className="hrs-hotel-card-footer">
                    <div className="hrs-hotel-pricing-box">
                      <span className="hrs-hotel-price-caption">Starting from</span>
                      <div className="hrs-hotel-price-value">
                        {formatPrice(minPrice)} <span className="hrs-hotel-night-unit">/ night</span>
                      </div>
                    </div>

                    <a onClick={() => handleClick(stay)} className="hrs-hotel-view-btn">
                      View Stay <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HandpickedRomanticStays;